import React from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import Mermaid from './Mermaid'
import QuickLinks from './QuickLinks'
import DataView from './DataView'

type LinkDecision = { internalTo?: string; externalTo?: string }

export type MarkdownRendererProps = {
  markdown: string
  decideLink?: (href: string) => LinkDecision | undefined
}

export default function MarkdownRenderer({ markdown, decideLink }: MarkdownRendererProps) {
  const components = React.useMemo(
    () => ({
      pre({ children, ...props }: any) {
        // Render block widgets (mermaid, quicklinks) without the code-block <pre> wrapper.
        const child = Array.isArray(children) ? children[0] : children
        const className = child?.props?.className || ''
        if (/language-(mermaid|quicklinks|dataview)/.test(className)) return <>{child}</>
        return <pre {...props}>{children}</pre>
      },
      table({ children, ...props }: any) {
        // Wrap in a scroll container so wide tables scroll instead of overflowing the page,
        // while the table itself uses full-width table layout (headers fill completely).
        return (
          <div className="table-wrap">
            <table {...props}>{children}</table>
          </div>
        )
      },
      a({ href, children, ...props }: any) {
        const h = String(href || '')
        const decision = decideLink ? decideLink(h) : undefined
        if (decision?.internalTo) {
          // Use a normal anchor targeting our SPA route; the app router will intercept
          return (
            <a href={decision.internalTo} {...props}>
              {children}
            </a>
          )
        }
        const target = decision?.externalTo ?? h
        const isHash = target?.startsWith('#') || target?.startsWith('mailto:')
        if (isHash) {
          return (
            <a href={target} {...props}>
              {children}
            </a>
          )
        }
        const isAbsolute = /^(https?:)?\/\//i.test(target)
        return (
          <a href={target} {...(isAbsolute ? { target: '_blank', rel: 'noopener noreferrer' } : {})} {...props}>
            {children}
          </a>
        )
      },
      code({ inline, className, children, ...props }: any) {
        const txt = String(children || '')
        const langMatch = /language-([^\s]+)/.exec(className || '')
        const lang = (langMatch && langMatch[1]) || ''
        if (!inline && (lang.toLowerCase() === 'mermaid' || /^mermaid\s*\n/.test(txt))) {
          const chart = txt.replace(/^mermaid\s*\n/, '')
          return <Mermaid chart={chart}/>
        }
        if (!inline && lang.toLowerCase() === 'quicklinks') {
          return <QuickLinks raw={txt} decideLink={decideLink}/>
        }
        if (!inline && lang.toLowerCase() === 'dataview') {
          return <DataView raw={txt}/>
        }
        return (
          <code className={className} {...props}>
            {children}
          </code>
        )
      },
    }),
    [decideLink]
  )

  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components as any}>
      {markdown}
    </ReactMarkdown>
  )
}
